import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from '@tanstack/react-router'
import { useTranslation } from '@/hooks/useTypedTranslation'
import { useAuth } from '@/hooks/useAuth'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { FileText, FolderOpen, Upload, ArrowLeft, LogOut, Loader2, Settings, MessageSquare } from 'lucide-react'
import { ProjectManager } from './ProjectManager'
import { BlogManager } from './BlogManager'
import { MarkdownImporter } from './MarkdownImporter'
import { SiteConfigManager } from './SiteConfigManager'
import { FeishuSettings } from './FeishuSettings'

const VALID_TABS = ['projects', 'blogs', 'config', 'feishu', 'import'];

export function AdminDashboard() {
  const { t } = useTranslation()
  const { user, loading, isAdmin, signOut } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const urlSearch = new URLSearchParams(location.search)
  
  // Get initial tab from URL or default to 'projects'
  const tabFromUrl = urlSearch.get('tab')
  const initialTab = tabFromUrl && VALID_TABS.includes(tabFromUrl) ? tabFromUrl : 'projects'
  const [activeTab, setActiveTab] = useState(initialTab)
  
  // Sync URL params with tab changes
  useEffect(() => {
    const current = new URLSearchParams(location.search).get('tab')
    if (current && VALID_TABS.includes(current) && current !== activeTab) {
      setActiveTab(tabFromUrl)
    }
  }, [location.search])
  
  const handleTabChange = (tab: string) => {
    setActiveTab(tab)
    navigate({ to: `/admin?tab=${encodeURIComponent(tab)}`, replace: true })
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">{t('admin.accessDenied.title')}</h1>
          <p className="text-muted-foreground mb-6">{t('admin.accessDenied.message')}</p>
          <Button asChild variant="outline">
            <Link to="/">{t('admin.accessDenied.backToHome')}</Link>
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-xl font-bold">{t('admin.dashboard.title')}</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={() => signOut()}>
              <LogOut className="w-4 h-4 mr-2" />
              {t('admin.header.signOut')}
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="projects" className="flex items-center gap-2">
              <FolderOpen className="h-4 w-4" />
              {t('admin.tabs.projects')}
            </TabsTrigger>
            <TabsTrigger value="blogs" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              {t('admin.tabs.blogs')}
            </TabsTrigger>
            <TabsTrigger value="config" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Config
            </TabsTrigger>
            <TabsTrigger value="feishu" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              飞书
            </TabsTrigger>
            <TabsTrigger value="import" className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              {t('admin.tabs.import')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="projects" className="space-y-6">
            <ProjectManager />
          </TabsContent>

          <TabsContent value="blogs" className="space-y-6">
            <BlogManager />
          </TabsContent>

          <TabsContent value="config" className="space-y-6">
            <SiteConfigManager />
          </TabsContent>

          <TabsContent value="feishu" className="space-y-6">
            <FeishuSettings />
          </TabsContent>

          <TabsContent value="import" className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold">{t('admin.import.title')}</h2>
              <p className="text-muted-foreground">{t('admin.import.description')}</p>
            </div>
            
            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FolderOpen className="h-5 w-5" />
                    {t('admin.import.projects.title')}
                  </CardTitle>
                  <CardDescription>
                    {t('admin.import.projects.description')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <MarkdownImporter type="project" />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    {t('admin.import.blogs.title')}
                  </CardTitle>
                  <CardDescription>
                    {t('admin.import.blogs.description')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <MarkdownImporter type="blog" />
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
